/**
 * Reviews carousel used by the `product-reviews-carousel` block.
 *
 * One review card is visible at a time. Slides live in a horizontally
 * scrollable track with scroll snapping, so swiping works natively on touch
 * devices and the arrows simply scroll to the previous/next slide, wrapping
 * around at both ends.
 *
 * With `data-source="app"` the slides are built from `window.trReviewsProduct`,
 * the payload the Trustify app embed prints on every product page. Anything
 * already inside the track is a merchant-written fallback and is only replaced
 * when the app actually returns reviews.
 */

const AVATAR_COLORS = ['#E8EDF5', '#F5E9E2', '#E7F1EA', '#F2E9F5', '#F5F1E2', '#E6F0F5'];

const STAR_SVG =
  '<svg class="reviews-carousel__star" viewBox="0 0 24 24" role="presentation" focusable="false">' +
  '<path d="M12 2.4l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 17.42l-5.88 3.09 1.12-6.55L2.48 9.32l6.58-.96L12 2.4z" />' +
  '</svg>';

const VERIFIED_SVG =
  '<svg class="reviews-carousel__verified" viewBox="0 0 24 24" role="presentation" focusable="false">' +
  '<circle cx="12" cy="12" r="10" fill="currentColor" />' +
  '<path d="M7.4 12.3l3.1 3.1 6.1-6.4" fill="none" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" />' +
  '</svg>';

class ProductReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reviews-track]');
    if (!this.track) return;

    this.previousButton = this.querySelector('[data-reviews-previous]');
    this.nextButton = this.querySelector('[data-reviews-next]');

    if (this.dataset.source === 'app') {
      this.#renderAppReviews();
    }

    this.slides = Array.from(this.querySelectorAll('[data-reviews-slide]'));

    // No reviews to show: leave the block hidden rather than render an empty card.
    if (this.slides.length === 0) {
      this.hidden = true;
      if (this.dataset.source === 'app') this.#watchForLateReviews();
      return;
    }

    this.hidden = false;
    this.index = 0;

    const single = this.slides.length < 2;
    if (this.previousButton) this.previousButton.hidden = single;
    if (this.nextButton) this.nextButton.hidden = single;

    this.handlePrevious = () => this.#goTo(this.index - 1, true);
    this.handleNext = () => this.#goTo(this.index + 1, true);
    this.handleScroll = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.index = this.#closestIndex();
      }, 80);
    };
    this.pause = () => this.#stopAutoplay();
    this.resume = () => this.#startAutoplay();

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);

    this.#markVisibleSlide();
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.resume);
    clearTimeout(this.scrollTimeout);
    clearTimeout(this.retryTimeout);
    this.#stopAutoplay();
  }

  /* -------------------------------------------------------------- app data */

  /** @returns {Array<object>} Reviews that pass the block's filters. */
  #appReviews() {
    const reviews = window.trReviewsProduct?.reviews;
    if (!Array.isArray(reviews)) return [];

    const minRating = Number(this.dataset.minRating || 1);
    const requireText = this.dataset.requireText === 'true';
    const max = Number(this.dataset.maxReviews || 6);

    return reviews
      .filter((review) => {
        if (Number(review?.rating) < minRating) return false;
        if (requireText && !String(review?.content || '').trim()) return false;
        return true;
      })
      .slice(0, max);
  }

  #renderAppReviews() {
    const reviews = this.#appReviews();
    if (reviews.length === 0) return;

    const fragment = document.createDocumentFragment();
    reviews.forEach((review, index) => fragment.appendChild(this.#buildSlide(review, index)));

    this.track.replaceChildren(fragment);
  }

  /** The app hydrates its payload late on some pages; check a few times, then give up. */
  #watchForLateReviews(attempt = 0) {
    if (attempt > 10) return;

    this.retryTimeout = setTimeout(() => {
      if (!this.isConnected) return;
      if (this.#appReviews().length === 0) {
        this.#watchForLateReviews(attempt + 1);
        return;
      }
      this.connectedCallback();
    }, 300);
  }

  /**
   * @param {object} review - A review from the app payload.
   * @param {number} index - Position, used to vary the placeholder avatar color.
   * @returns {HTMLElement}
   */
  #buildSlide(review, index) {
    const name = String(review.reviewer_name || '').trim();
    const content = String(review.content || '').trim();
    const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
    const verified = this.dataset.verifiedBadge === 'auto' && review.is_verified === true;

    const slide = document.createElement('div');
    slide.className = 'reviews-carousel__slide';
    slide.setAttribute('data-reviews-slide', '');

    const card = document.createElement('article');
    card.className = 'reviews-carousel__card';

    if (review.avatar) {
      const image = document.createElement('img');
      image.className = 'reviews-carousel__avatar';
      image.src = review.avatar;
      image.alt = name;
      image.loading = 'lazy';
      card.appendChild(image);
    } else {
      const initial = document.createElement('span');
      initial.className = 'reviews-carousel__avatar reviews-carousel__avatar--initial';
      initial.style.backgroundColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
      initial.setAttribute('aria-hidden', 'true');
      initial.textContent = name.slice(0, 1).toUpperCase();
      card.appendChild(initial);
    }

    const body = document.createElement('div');
    body.className = 'reviews-carousel__content';

    const meta = document.createElement('div');
    meta.className = 'reviews-carousel__meta';

    if (name) {
      const nameElement = document.createElement('span');
      nameElement.className = 'reviews-carousel__name';
      nameElement.textContent = name;
      meta.appendChild(nameElement);
    }

    if (verified) {
      meta.insertAdjacentHTML('beforeend', VERIFIED_SVG);
    }

    const stars = document.createElement('span');
    stars.className = 'reviews-carousel__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${rating}/5`);
    stars.innerHTML =
      `<span class="reviews-carousel__stars-row" aria-hidden="true">${STAR_SVG.repeat(5)}</span>` +
      `<span class="reviews-carousel__stars-clip" style="width: ${rating * 20}%;" aria-hidden="true">` +
      `<span class="reviews-carousel__stars-row reviews-carousel__stars-row--filled">${STAR_SVG.repeat(5)}</span>` +
      `</span>`;
    meta.appendChild(stars);
    body.appendChild(meta);

    if (content) {
      const text = document.createElement('p');
      text.className = 'reviews-carousel__text';
      text.textContent = content;
      body.appendChild(text);
    }

    card.appendChild(body);
    slide.appendChild(card);
    return slide;
  }

  /* ------------------------------------------------------------- carousel */

  get #autoplayDelay() {
    return Number(this.dataset.autoplaySpeed || 0) * 1000;
  }

  #startAutoplay() {
    this.#stopAutoplay();
    if (this.slides.length < 2 || this.#autoplayDelay <= 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayTimer = setInterval(() => {
      if (document.hidden) return;
      this.#goTo(this.index + 1);
    }, this.#autoplayDelay);
  }

  #stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  #markVisibleSlide() {
    this.slides.forEach((slide, position) => {
      slide.setAttribute('aria-hidden', position === this.index ? 'false' : 'true');
    });
  }

  #closestIndex() {
    const { scrollLeft } = this.track;
    let closest = 0;
    let smallestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft - scrollLeft);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closest = index;
      }
    });

    return closest;
  }

  /**
   * @param {number} index - Slide to scroll to. Wraps around at both ends.
   * @param {boolean} [restartAutoplay] - Restarts the timer after a manual interaction.
   */
  #goTo(index, restartAutoplay = false) {
    const total = this.slides.length;
    this.index = ((index % total) + total) % total;

    const slide = this.slides[this.index];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    this.#markVisibleSlide();

    if (restartAutoplay) this.#startAutoplay();
  }
}

if (!customElements.get('product-reviews-carousel')) {
  customElements.define('product-reviews-carousel', ProductReviewsCarousel);
}
